/**
 * Where the card goes, as arithmetic (no DOM): TourHost measures, this
 * decides, and a test can check every corner of the screen without a browser.
 */
const GAP = 16;
const MARGIN = 12;
export const NARROW_PX = 720;

export function isNarrow(viewportWidth) {
  return viewportWidth < NARROW_PX;
}

/**
 * A data-tour target as a selector. `surface.thing` addresses one element;
 * `surface.thing:<key>` addresses the one among repeated elements whose
 * data-tour-key is <key> (a load's PO, a list's title).
 */
export function targetSelector(target) {
  const at = target.indexOf(':');
  const name = at === -1 ? target : target.slice(0, at);
  const key = at === -1 ? null : target.slice(at + 1);
  const quote = value => String(value).replace(/\\/g, '\\\\').replace(/"/g, '\\"');
  return key === null
    ? `[data-tour="${quote(name)}"]`
    : `[data-tour="${quote(name)}"][data-tour-key="${quote(key)}"]`;
}

/**
 * With the card DOCKED to the bottom of a phone, the page has to scroll so the
 * thing being pointed at sits in what is left of the screen above the card.
 * Returns how far to scroll down (negative: up). A target that fits is centred
 * in the free space; one that does not fit shows its TOP, just under the top
 * edge, because a heading or a map's first rows say what the thing is and its
 * tail does not.
 */
export function dockedScrollDelta({ target, viewportHeight, cardHeight }) {
  const free = Math.max(0, viewportHeight - cardHeight);
  const fits = target.height <= free - MARGIN * 2;
  const desiredTop = fits ? (free - target.height) / 2 : MARGIN;
  return Math.round(target.top - desiredTop);
}

const clamp = (value, min, max) => Math.min(Math.max(value, min), Math.max(min, max));

/**
 * The card's top-left, beside the target on the preferred side, flipped to
 * the opposite side when it would not fit, and always kept on screen.
 */
export function cardPosition({ target, card, viewport, placement = 'bottom' }) {
  const fits = {
    top: target.top - GAP - card.height >= MARGIN,
    bottom: target.bottom + GAP + card.height <= viewport.height - MARGIN,
    left: target.left - GAP - card.width >= MARGIN,
    right: target.right + GAP + card.width <= viewport.width - MARGIN,
  };
  const opposite = { top: 'bottom', bottom: 'top', left: 'right', right: 'left' };
  const order = [placement, opposite[placement], 'bottom', 'top', 'right', 'left'].filter(Boolean);
  const side = order.find(s => fits[s]) || 'inside';

  const centreX = target.left + target.width / 2 - card.width / 2;
  const centreY = target.top + target.height / 2 - card.height / 2;
  let top;
  let left;
  if (side === 'top') [top, left] = [target.top - GAP - card.height, centreX];
  else if (side === 'bottom') [top, left] = [target.bottom + GAP, centreX];
  else if (side === 'left') [top, left] = [centreY, target.left - GAP - card.width];
  else if (side === 'right') [top, left] = [centreY, target.right + GAP];
  // A target that fills the screen (the map, the board): sit in its lower right.
  else
    [top, left] = [
      viewport.height - card.height - MARGIN * 2,
      viewport.width - card.width - MARGIN * 2,
    ];

  return {
    top: Math.round(clamp(top, MARGIN, viewport.height - card.height - MARGIN)),
    left: Math.round(clamp(left, MARGIN, viewport.width - card.width - MARGIN)),
    side,
  };
}

/**
 * The part of the screen a walkthrough can show a field in, as viewport
 * pixels: the visible part of the box that scrolls it (#content on a desk,
 * the page on a phone, a dialog's own body), less the card when the card sits
 * in the same columns as the field. Above the scrolling box is the app's
 * header, which never scrolls; nothing is ever shown under it.
 */
export function freeRegion({ scroller = null, viewport, card = null, target = null, gap = GAP }) {
  const top = Math.max(0, scroller ? scroller.top : 0);
  let bottom = Math.min(viewport.height, scroller ? scroller.bottom : viewport.height);
  if (card && target) {
    const sameColumns = target.left < card.right + gap && target.right > card.left - gap;
    if (sameColumns && card.top > top) bottom = Math.min(bottom, card.top - gap);
  }
  return { top, bottom: Math.max(top, bottom) };
}

/**
 * How far to scroll (down positive) so `target` sits wholly inside `region`
 * with `margin` to spare. Nothing when it already does: a field that is in
 * view stays put, so moving from one field to the next one below it does not
 * scroll the form under the visitor's eyes. Otherwise the target goes a third
 * of the way down the region, which leaves the fields after it in view too;
 * one taller than the region shows its top.
 */
export function revealDelta({ target, region, margin = MARGIN }) {
  const top = region.top + margin;
  const bottom = region.bottom - margin;
  if (target.top >= top && target.bottom <= bottom) return 0;
  const space = bottom - top;
  if (target.height >= space) return Math.round(target.top - top);
  return Math.round(target.top - (top + (space - target.height) / 3));
}

/**
 * The part of `rect` that every rect in `clips` also contains, or null when
 * none of it is. The ring is drawn in the viewport, over everything, from the
 * control's own bounding box -- but a control inside a dialog's scrolling
 * body (the driver table on Assign) can be taller than the body, and its box
 * then runs past the dialog's edge and over the page chrome above it (seen on
 * the live demo, 2026-10-13). Clipped to its scrolling ancestors and the
 * viewport, the ring stops where the eye stops seeing the control.
 */
export function clipRect(rect, clips) {
  if (!rect) return null;
  let { top, left } = rect;
  let right = rect.left + rect.width;
  let bottom = rect.top + rect.height;
  for (const clip of clips || []) {
    if (!clip) continue;
    top = Math.max(top, clip.top);
    left = Math.max(left, clip.left);
    right = Math.min(right, clip.left + clip.width);
    bottom = Math.min(bottom, clip.top + clip.height);
  }
  if (right - left <= 0 || bottom - top <= 0) return null;
  return { top, left, width: right - left, height: bottom - top };
}

/**
 * A walkthrough card's corners, in the order they are tried. The card keeps
 * one corner (Mark, 2026-10-12: "maintain position of the popover") and moves
 * only when it would cover something the visitor has to see or press.
 */
export const CORNERS = Object.freeze(['bottom-right', 'bottom-left', 'top-right', 'top-left']);

/**
 * The card's box in `corner`: `inset` from the screen's edges, and a top
 * corner `top` px down, below the app's header, which never scrolls.
 */
export function cornerRect(corner, { viewport, card, inset, top = 0 }) {
  const left = corner.endsWith('left') ? inset : viewport.width - inset - card.width;
  const y = corner.startsWith('top') ? top + inset : viewport.height - inset - card.height;
  return { top: y, left, right: left + card.width, bottom: y + card.height };
}

const edges = r =>
  'right' in r && 'bottom' in r
    ? r
    : { top: r.top, left: r.left, right: r.left + r.width, bottom: r.top + r.height };

function overlapArea(a, b) {
  const w = Math.min(a.right, b.right) - Math.max(a.left, b.left);
  const h = Math.min(a.bottom, b.bottom) - Math.max(a.top, b.top);
  return w > 0 && h > 0 ? w * h : 0;
}

/**
 * Which corner the card goes in, given what it must not cover: the ringed
 * control, an open menu's items, an open dialog's controls (found on the live
 * demo, 2026-10-22: in the bottom-right corner it hid "Set driver or
 * subhauler", "Set truck" and the truck dialog's Save on any screen under
 * about 945px tall). The current corner is kept while it is clear, so the
 * card does not hop as the page settles; otherwise the first clear corner;
 * when none is, the one that covers least. Rects are `{top, left, width,
 * height}` or `{top, left, right, bottom}`.
 */
export function pickCorner({
  viewport,
  card,
  avoid = [],
  current = CORNERS[0],
  inset,
  top = 0,
  gap = 8,
}) {
  const areas = avoid.filter(Boolean).map(edges);
  const cost = corner => {
    const r = cornerRect(corner, { viewport, card, inset, top });
    const padded = {
      top: r.top - gap,
      left: r.left - gap,
      right: r.right + gap,
      bottom: r.bottom + gap,
    };
    return areas.reduce((sum, a) => sum + overlapArea(padded, a), 0);
  };
  const start = CORNERS.includes(current) ? current : CORNERS[0];
  let best = start;
  let bestCost = cost(start);
  if (bestCost === 0) return start;
  for (const corner of CORNERS) {
    const c = cost(corner);
    if (c === 0) return corner;
    if (c < bestCost) [best, bestCost] = [corner, c];
  }
  return best;
}
